/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/
import {
    ISignUpEmailVerification,
    ISignUpItem,
    ISignUpLoading,
    ISignUpLocalAccount,
    ISignUpViewProps
} from '@msdyn365-commerce-modules/account-management';
import { Module, Node } from '@msdyn365-commerce-modules/utilities';
import * as Msdyn365 from '@msdyn365-commerce/core';
import * as React from 'react';

const SignUpItem: React.FC<ISignUpItem> = ({ wrapper, label, errorMessage, input }) => {
    return (
        <Node {...wrapper}>
            {label}
            {errorMessage}
            {input}
        </Node>
    );
};

const SignUpEmailVerification: React.FC<ISignUpEmailVerification> = ({ email, buttonWrapper, buttons, successMessage, errorMessage }) => {
    return (
        <>
            <SignUpItem {...email} key={email.key} />
            {successMessage &&
                successMessage.map((message: React.ReactNode) => {
                    return <>{message}</>;
                })}
            {errorMessage &&
                errorMessage.map((message: React.ReactNode) => {
                    return <>{message}</>;
                })}
            <Node {...buttonWrapper}>
                {buttons &&
                    buttons.map((button: React.ReactNode) => {
                        return <>{button}</>;
                    })}
            </Node>
        </>
    );
};

const LocalAccount: React.FC<ISignUpLocalAccount> = (
    { localAccount, items, emailVerification, buttons, errorMessage, disclaimer },
    props
) => {
    const europe = ['/fr', '/de'];
    const locale = props?.context?.request?.locale;
    const uiLocales = props?.context?.request?.query.ui_locales;
    const regionCRM = europe.includes(uiLocales) ? uiLocales : props.config.RegionCRM;

    const [birthDate, setBirthDate] = React.useState('');
    const [formatDateState, setFormatDateState] = React.useState(true);
    const pattern = /^((1[6789]|[2-9][0-9])[0-9]{2}\/(0[13578]|1[02])\/(0[1-9]|[12][0-9]|3[01]))$|^((1[6789]|[2-9][0-9])[0-9]{2}\/(0[469]|11)\/(0[1-9]|[12][0-9]|30))$|^((16|[248][048]|[3579][26])00)|(1[6789]|[2-9][0-9])(0[48]|[13579][26]|[2468][048])\/02\/(0[1-9]|1[0-9]|2[0-9])$|^(1[6789]|[2-9][0-9])[0-9]{2}\/02\/(0[1-9]|1[0-9]|2[0-8])$/gm;
    const patternUS = /^((0[13578]|1[02])\/(0[1-9]|[12][0-9]|3[01]))$|^((0[469]|11)\/(0[1-9]|[12][0-9]|30))$|^02\/(0[1-9]|1[0-9]|2[0-9])$/gm;

    const checkDate = (event: React.KeyboardEvent<HTMLInputElement>) => {
        const value = event.currentTarget.value;
        const today = new Date();
        if (value !== '' && (!value.match(locale !== 'en-US' ? pattern : patternUS) || today < new Date(value))) {
            setFormatDateState(false);
        } else {
            setFormatDateState(true);
        }
        if (locale === 'en-US') {
            setBirthDate(`1900/${value}`);
        }
    };
    if (typeof window !== 'undefined') {
        const signUpButton: HTMLElement = document.getElementById('continue') as HTMLElement;

        if (signUpButton !== undefined) {
            signUpButton.onclick = e => {
                if (!formatDateState) {
                    e.preventDefault();
                    e.stopPropagation();
                }
            };
        }
    }

    return (
        <Node {...localAccount}>
            {items &&
                items.map((item: ISignUpItem) => {
                    if (item.key === 'email' && emailVerification.isRequired) {
                        return (
                            <Node {...emailVerification.verificationControlWrapper}>
                                <SignUpItem {...item} key={item.key} />
                                <div className={'ms-sign-up__account-item ms-sign-up__account-item-birthday entry-item attrEntry'}>
                                    <label className={'ms-sign-up__account-item-label'} htmlFor='extension_Birthday'>
                                        {props.resources.birthdayLabelText}
                                        {locale !== 'en-US' ? '**  (yyyy/mm/dd)' : '**  (mm/dd)'}
                                    </label>
                                    {formatDateState === false ? (
                                        <p className='ms-sign-up__item-error-text'>{props.resources.birthdayErrorText}</p>
                                    ) : (
                                        ''
                                    )}
                                    {locale !== 'en-US' ? (
                                        <input
                                            type='text'
                                            id='extension_Birthday'
                                            name='extension_Birthday'
                                            maxLength={10}
                                            className='ms-sign-up__account-item-input ms-sign-up__account-item-birthday'
                                            onKeyUp={e => checkDate(e)}
                                            onPaste={e => e.preventDefault()}
                                        />
                                    ) : (
                                        <>
                                            <input
                                                type='text'
                                                id='extension_Birthday_F'
                                                maxLength={5}
                                                name='extension_Birthday_F'
                                                className='ms-sign-up__account-item-input ms-sign-up__account-item-birthday'
                                                onKeyUp={e => checkDate(e)}
                                                onPaste={e => e.preventDefault()}
                                            />
                                            <input
                                                type='text'
                                                id='extension_Birthday'
                                                maxLength={10}
                                                name='extension_Birthday'
                                                className='ms-sign-up__account-item-input ms-sign-up__account-item-birthday hidden'
                                                value={birthDate}
                                            />
                                        </>
                                    )}

                                    <Msdyn365.RichTextComponent text={props.config.BirthdayText} />
                                </div>
                                <div className={'region_crm_ctn'}>
                                    <input type='text' name='extension_RegionCRM' id='extension_RegionCRM' value={regionCRM} />
                                </div>
                                <div className='inputBox privacy_field_ctn'>
                                    <label>
                                        <input
                                            type='checkbox'
                                            aria-checked={props.config.renderThirdPartyPrivacy ? 'false' : 'true'}
                                            defaultChecked={props.config.renderThirdPartyPrivacy ? false : true}
                                            name='extension_GenericMarketing'
                                            value={'true'}
                                        />
                                        <Msdyn365.RichTextComponent text={props.config.textGenericMarketing} />
                                    </label>
                                </div>
                                {props.config.renderThirdPartyPrivacy && (
                                    <div className='inputBox privacy_field_ctn'>
                                        <label>
                                            <input
                                                type='checkbox'
                                                aria-checked='false'
                                                defaultChecked={false}
                                                name='extension_ThirdParty'
                                                value={'true'}
                                                required
                                            />
                                            <Msdyn365.RichTextComponent text={props.config.textThirdParty} />
                                        </label>
                                    </div>
                                )}
                                <SignUpEmailVerification {...emailVerification} />
                            </Node>
                        );
                    }
                    return <SignUpItem {...item} key={item.key} />;
                })}
            {errorMessage &&
                errorMessage.map((error: React.ReactNode, index: number) => {
                    return <React.Fragment key={index}>{error}</React.Fragment>;
                })}
            {buttons &&
                buttons.map((button: React.ReactNode, index: number) => {
                    return <React.Fragment key={index}>{button}</React.Fragment>;
                })}
            {disclaimer}
        </Node>
    );
};

const SignUpLoading: React.FC<ISignUpLoading> = ({ modal, modalBody, icon, message }) => {
    return (
        <Node {...modal}>
            <Node {...modalBody}>
                {icon}
                {message}
            </Node>
        </Node>
    );
};

const SignUpView: React.FC<ISignUpViewProps> = props => {
    const { signUp, viewState, loading, defaultAADConainer, aadConainer, heading, signUpLocalAccount } = props;

    return (
        <Module {...signUp}>
            {viewState.isShowLoading && <Node {...defaultAADConainer} />}
            {viewState.isShowLoading && <SignUpLoading {...loading} />}
            <Node {...aadConainer}>
                {heading}
                {LocalAccount(signUpLocalAccount, props)}
            </Node>
        </Module>
    );
};

export default SignUpView;
